import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Save, Building2 } from "lucide-react";
import { maskPhone, waLink } from "@/lib/format";

const empty = { name: "Alpha Metais – Usinagem CNC", cnpj: "", address: "", phone: "", whatsapp: "", email: "", website: "" };

export default function Settings() {
  const [form, setForm] = useState(empty);
  const [hasLogo, setHasLogo] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setForm({ ...empty, ...data });
      setHasLogo(!!data?.has_logo);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", form);
      toast.success("Dados da empresa salvos");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      setPreview(URL.createObjectURL(file));
      toast.success("Logo enviado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logoUrl = preview || (hasLogo ? `${process.env.REACT_APP_BACKEND_URL}/api/settings/logo?t=${Date.now()}` : null);

  return (
    <div className="space-y-5">
      <header>
        <div className="text-[11px] mono uppercase tracking-[0.3em] text-orange-400/80">
          configurações · empresa
        </div>
        <h1 className="display text-3xl font-bold mt-1">Dados da Empresa</h1>
        <p className="text-sm text-slate-400 mt-1">Estas informações aparecem no cabeçalho do PDF de orçamento.</p>
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-tactical rounded-lg p-5 lg:col-span-2">
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs uppercase text-slate-400">Nome / Razão Social</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400">CNPJ</label>
              <input
                value={form.cnpj}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm mono"
                data-testid="settings-cnpj-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400">Telefone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm mono"
                placeholder="(00) 00000-0000"
                data-testid="settings-phone-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400">WhatsApp oficial</label>
              <input
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: maskPhone(e.target.value) })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm mono"
                placeholder="(00) 00000-0000"
                data-testid="settings-whatsapp-input"
              />
              {waLink(form.whatsapp) && (
                <a href={waLink(form.whatsapp)} target="_blank" rel="noreferrer"
                  className="mt-1 block text-[10px] text-emerald-400 hover:text-emerald-300 mono break-all transition-colors"
                  data-testid="settings-whatsapp-link">
                  Link gerado: {waLink(form.whatsapp)}
                </a>
              )}
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase text-slate-400">Endereço completo</label>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                data-testid="settings-address-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400">Website</label>
              <input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="col-span-2 flex justify-end">
              <button
                type="submit"
                className="pill-accent rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-2"
                data-testid="settings-save-button"
              >
                <Save className="w-4 h-4" /> Salvar
              </button>
            </div>
          </form>
        </div>

        <div className="card-tactical rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-orange-300" />
            <div className="text-[11px] mono uppercase tracking-widest text-orange-400/80">
              logotipo
            </div>
          </div>
          <div className="aspect-square rounded-md bg-white flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="logo"
                className="max-w-full max-h-full object-contain"
                data-testid="settings-logo-preview"
              />
            ) : (
              <div className="text-xs text-slate-400">Sem logo enviado</div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadLogo(e.target.files?.[0])}
            data-testid="settings-logo-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="mt-3 w-full pill-accent rounded-md py-2 text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="settings-logo-upload-button"
          >
            <Upload className="w-4 h-4" /> Enviar logo (PNG)
          </button>
          <p className="text-[11px] text-slate-500 mt-2">Recomendado PNG com fundo transparente, 500×500px.</p>
        </div>
      </div>
    </div>
  );
}
